import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiCheckCircle, FiTrendingUp, FiZap, FiClock, FiBook, FiInfo } from 'react-icons/fi';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import * as S from '../../styles/inlineStyles';
import SubmissionReviewModal from '../../components/student/SubmissionReviewModal';

// ─── Green / Mint palette ───────────────────────────────────────────────────
const C = {
    textPrimary: '#1a3326',
    textSecondary: '#4a7060',
    textLight: '#8ab5a0',
    border: '#c8e6d8',
    primary: '#27a065',
    white: '#ffffff',
};
const font = "'Outfit', sans-serif";

const statCards = (stats) => [
    {
        key: 'upcoming',
        label: 'Upcoming Exams',
        value: stats.upcomingExams,
        icon: <FiCalendar size={22} />,
        gradient: 'linear-gradient(135deg, #6edba8 0%, #27a065 100%)',
        bg: '#edfaf4',
        border: '#b8e8d0',
        shadow: 'rgba(39,160,101,0.18)',
        route: '/student/exams',
    },
    {
        key: 'completed',
        label: 'Completed',
        value: stats.completedExams,
        icon: <FiCheckCircle size={22} />,
        gradient: 'linear-gradient(135deg, #a8f0cc 0%, #1e8c58 100%)',
        bg: '#e8f8f1',
        border: '#a8dfc0',
        shadow: 'rgba(30,140,88,0.18)',
        route: '/student/results',
    },
    {
        key: 'score',
        label: 'Avg. Score',
        value: `${stats.averageScore}%`,
        icon: <FiTrendingUp size={22} />,
        gradient: 'linear-gradient(135deg, #b8f0d8 0%, #159a60 100%)',
        bg: '#eafaf3',
        border: '#b0e0c8',
        shadow: 'rgba(21,154,96,0.18)',
        route: '/student/results',
    },
    {
        key: 'performance',
        label: 'Performance',
        value: stats.averageScore >= 75 ? 'Excellent' : stats.averageScore >= 50 ? 'Good' : 'Keep Going',
        icon: <FiZap size={22} />,
        gradient: 'linear-gradient(135deg, #d5f3d8 0%, #2db870 100%)',
        bg: '#edfaf1',
        border: '#b5e8c8',
        shadow: 'rgba(45,184,112,0.18)',
        route: null,
    },
];

const StudentDashboard = () => {
    const [stats, setStats] = useState({ upcomingExams: 0, completedExams: 0, averageScore: 0 });
    const [upcomingExams, setUpcomingExams] = useState([]);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [viewingDetails, setViewingDetails] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => { fetchDashboard(); }, []);

    const fetchDashboard = async () => {
        try {
            const [examRes, resultRes] = await Promise.all([
                api.get('/api/student/upcoming-exams'),
                api.get('/api/student/results'),
            ]);
            const exams = examRes.data.exams || [];
            const marks = resultRes.data.results || [];

            // 1. Truly Upcoming: Not submitted AND state is not ENDED
            const upcoming = exams.filter(e => !e.alreadySubmitted && e.state !== 'ENDED');

            // 2. Completed: Any exam that has ended OR has been submitted (regardless of whether they joined)
            const completedCount = exams.filter(e => e.alreadySubmitted || e.state === 'ENDED').length;

            // 3. Results: Combine actual results with synthesized ones for ALL "Completed" exams (Submitted or Ended)
            const combinedResults = [...marks];
            exams.forEach(e => {
                const isCompleted = e.alreadySubmitted || e.state === 'ENDED';
                // Check if this exam is already represented in the results list
                const alreadyInResults = marks.some(m => m.paper_id === e.paperId || (m.subject_code === e.subject_code && m.id === e.id));

                if (isCompleted && !alreadyInResults) {
                    combinedResults.push({
                        id: `synth-${e.id}`,
                        paper_id: e.paperId,
                        title: e.subject_name,
                        subject_name: e.subject_name,
                        subject_code: e.subject_code,
                        total_marks: e.total_marks,
                        submitted_at: e.state === 'ENDED' ? e.endTime : new Date().toISOString(),
                        graded_at: null,
                        status: e.alreadySubmitted ? 'completed' : 'MISSED'
                    });
                }
            });

            setUpcomingExams(upcoming.slice(0, 3));
            setResults(combinedResults.slice(0, 3));

            const evaluatedMarks = marks.filter(m => m.graded_at);
            const avg = evaluatedMarks.length > 0
                ? Math.round(evaluatedMarks.reduce((sum, m) => sum + ((m.total_score / m.total_marks) * 100), 0) / evaluatedMarks.length)
                : 0;

            setStats({
                upcomingExams: upcoming.length,
                completedExams: completedCount,
                averageScore: avg
            });
        } catch (err) {
            console.error('Failed to fetch dashboard', err);
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = async (subId) => {
        if (!subId || subId.toString().startsWith('synth-')) {
            alert("Submission data not found for this entry.");
            return;
        }
        setDetailLoading(true);
        try {
            const res = await api.get(`/api/student/submissions/${subId}/details`);
            setSelectedSubmission(res.data);
            setViewingDetails(true);
        } catch (err) {
            console.error('Failed to fetch submission details');
            alert("Failed to load details. It's possible the data is still being processed.");
        } finally {
            setDetailLoading(false);
        }
    };

    const getTimeRemaining = (startTime) => {
        const diff = new Date(startTime) - new Date();
        if (diff <= 0) return 'Started';
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    };

    if (loading) return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 16 }}>
            <div style={{ width: 40, height: 40, border: `4px solid #c8e6d8`, borderTopColor: '#27a065', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ fontSize: 14, color: C.textSecondary, fontFamily: font }}>Loading your dashboard...</p>
        </div>
    );

    return (
        <div className="page-enter main-content">

            {/* ─── Welcome Banner ─── */}
            <div style={{
                background: 'linear-gradient(135deg, #d5f3d8 0%, #edfaf4 60%, #f0fdf4 100%)',
                borderRadius: 20,
                padding: '22px 24px',
                marginBottom: 24,
                border: '1.5px solid #c8e6d8',
                boxShadow: '0 4px 20px rgba(39,160,101,0.10)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 16,
            }}>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 800, color: C.textPrimary, fontFamily: font, margin: '0 0 4px', letterSpacing: -0.5 }}>
                        👋 Hello, {user?.name || 'Student'}!
                    </h1>
                    <p style={{ fontSize: 13, color: C.textSecondary, fontFamily: font, margin: 0 }}>
                        Welcome back · Here's your academic overview
                    </p>
                </div>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: 'white', padding: '10px 18px', borderRadius: 14,
                    boxShadow: '0 4px 14px rgba(39,160,101,0.12)',
                    border: '1.5px solid #c8e6d8',
                }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: 12,
                        background: 'linear-gradient(135deg, #6edba8, #27a065)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: 20,
                    }}>🎓</div>
                    <div>
                        <div style={{ fontSize: 11, color: C.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: font }}>Total Exams</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: C.textPrimary, fontFamily: font }}>{stats.upcomingExams + stats.completedExams}</div>
                    </div>
                </div>
            </div>

            {/* ─── Stat Cards ─── */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
                gap: 14,
                marginBottom: 28,
            }}>
                {statCards(stats).map(card => (
                    <div
                        key={card.key}
                        onClick={() => card.route && navigate(card.route)}
                        style={{
                            background: card.bg,
                            borderRadius: 18,
                            padding: '20px 18px',
                            border: `1.5px solid ${card.border}`,
                            cursor: card.route ? 'pointer' : 'default',
                            boxShadow: `0 4px 18px ${card.shadow}`,
                            transition: 'all 0.25s ease',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                        onMouseEnter={e => { if (card.route) { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 10px 28px ${card.shadow}`; } }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 4px 18px ${card.shadow}`; }}
                    >
                        {/* Single icon */}
                        <div style={{
                            width: 46, height: 46, borderRadius: 13,
                            background: card.gradient,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', marginBottom: 14,
                            boxShadow: `0 4px 14px ${card.shadow}`,
                        }}>
                            {card.icon}
                        </div>

                        <div style={{ fontSize: 28, fontWeight: 800, color: C.textPrimary, fontFamily: font, lineHeight: 1, marginBottom: 6 }}>
                            {card.value}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: 0.6, fontFamily: font }}>
                            {card.label}
                        </div>

                        {/* Subtle decorative circle */}
                        <div style={{
                            position: 'absolute', bottom: -18, right: -18,
                            width: 72, height: 72, borderRadius: '50%',
                            background: card.gradient, opacity: 0.15, pointerEvents: 'none',
                        }} />
                    </div>
                ))}
            </div>

            {/* ─── Upcoming Exams ─── */}
            <div style={{ marginBottom: 26 }}>
                <h2 style={{ fontSize: 17, fontWeight: 800, color: C.textPrimary, fontFamily: font, margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 5, height: 20, borderRadius: 4, background: 'linear-gradient(180deg,#6edba8,#27a065)', display: 'inline-block' }} />
                    Upcoming Exams
                </h2>
                {upcomingExams.length === 0 ? (
                    <div style={{ background: '#fff', borderRadius: 18, border: '1.5px solid #c8e6d8', padding: '40px 20px', textAlign: 'center', boxShadow: '0 2px 10px rgba(39,160,101,0.07)' }}>
                        <div style={{ fontSize: 40, marginBottom: 10 }}>📅</div>
                        <h3 style={{ fontSize: 16, fontWeight: 700, color: C.textPrimary, fontFamily: font, margin: '0 0 6px' }}>No Upcoming Exams</h3>
                        <p style={{ fontSize: 13, color: C.textSecondary, fontFamily: font, margin: 0 }}>No examinations are currently scheduled.</p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                        {upcomingExams.map(exam => (
                            <div key={exam.id} className="card-hover" style={{
                                background: '#fff', borderRadius: 18,
                                border: '1.5px solid #c8e6d8',
                                overflow: 'hidden',
                                boxShadow: '0 4px 16px rgba(39,160,101,0.08)',
                            }}>
                                <div style={{ height: 5, background: 'linear-gradient(90deg,#6edba8,#d5f3d8)' }} />
                                <div style={{ padding: '16px 18px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
                                        <h3 style={{ fontFamily: font, fontSize: 15, fontWeight: 700, margin: 0, color: C.textPrimary }}>{exam.subject_name} ({exam.subject_code})</h3>
                                        <span style={mkBadge(exam.state)}>{exam.state}</span>
                                    </div>
                                    <div style={{ fontSize: 13, color: C.primary, fontWeight: 600, marginBottom: 12 }}>📖 {exam.department}</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: C.textSecondary }}>
                                            <FiCalendar size={13} /> {exam.startTime ? new Date(exam.startTime).toLocaleString() : `${exam.exam_date} ${exam.exam_time}`}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: C.textSecondary }}>
                                                <FiClock size={13} /> {exam.duration} minutes
                                            </div>
                                            <div style={{ fontSize: 11, fontWeight: 700, background: '#f0fdf4', color: C.primary, padding: '2px 8px', borderRadius: 6, border: '1px solid #c8e6d8' }}>
                                                {exam.total_marks} Marks
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <span style={{ fontSize: 12, color: C.textLight }}>
                                            ⏰ <strong style={{ color: C.primary }}>{exam.startTime ? getTimeRemaining(exam.startTime) : 'Pending'}</strong>
                                        </span>
                                        {exam.state === 'LIVE' && (
                                            <button onClick={() => navigate(`/student/exam/${exam.id}`)} style={{
                                                padding: '7px 16px', fontSize: 12, fontWeight: 700,
                                                background: 'linear-gradient(135deg,#6edba8,#27a065)',
                                                color: '#fff', border: 'none', borderRadius: 10,
                                                cursor: 'pointer', fontFamily: font,
                                                boxShadow: '0 4px 12px rgba(39,160,101,0.30)',
                                                transition: 'all 0.2s ease',
                                            }}>Enter Exam →</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ─── Recent Results ─── */}
            <div style={{
                background: '#fff', borderRadius: 18,
                border: '1.5px solid #c8e6d8',
                overflow: 'hidden',
                boxShadow: '0 4px 16px rgba(39,160,101,0.07)',
            }}>
                <div style={{
                    padding: '16px 20px', borderBottom: '1px solid #c8e6d8',
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontFamily: font, fontWeight: 700, fontSize: 15, color: C.textPrimary,
                    background: 'linear-gradient(90deg,#edfaf4,#fff)',
                }}>
                    <span style={{ width: 5, height: 20, borderRadius: 4, background: 'linear-gradient(180deg,#6edba8,#27a065)', display: 'inline-block' }} />
                    Recent Results
                </div>
                <div style={{ padding: 0, overflowX: 'auto' }} className="responsive-table-wrap">
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: font, fontSize: 14 }}>
                        <thead>
                            <tr>
                                {['Exam', 'Subject', 'Score', 'Status'].map(h => (
                                    <th key={h} style={{
                                        padding: '11px 16px', textAlign: 'left', fontWeight: 700,
                                        fontSize: 11, color: C.textSecondary, textTransform: 'uppercase',
                                        letterSpacing: 1, borderBottom: '2px solid #c8e6d8', background: '#edfaf4',
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {results.length === 0 ? (
                                <tr><td colSpan={4} style={{ padding: '32px 16px', textAlign: 'center', color: C.textSecondary, fontFamily: font }}>
                                    No results available yet.
                                </td></tr>
                            ) : results.map(r => {
                                const isSynth = r.id.toString().startsWith('synth-');
                                const isMissed = r.status === 'MISSED';
                                return (
                                    <tr key={r.id} className={isSynth || isMissed ? "" : "tr-hover"} onClick={() => !isSynth && !isMissed && handleViewDetails(r.id)} style={{ cursor: isSynth || isMissed ? 'default' : 'pointer' }}>
                                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #d5f3d8', color: C.textPrimary, fontWeight: 600 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                {r.title || 'N/A'}
                                                {!isSynth && !isMissed && <FiInfo size={12} color={C.textLight} />}
                                            </div>
                                        </td>
                                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #d5f3d8', color: C.textSecondary }}>{r.subject_name || 'N/A'}</td>
                                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #d5f3d8' }}>
                                            {r.graded_at ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 8, background: 'rgba(39,160,101,0.10)', color: '#27a065', fontWeight: 700, fontSize: 13 }}>
                                                    <FiBook size={13} />{r.total_score}/{r.total_marks}
                                                </span>
                                            ) : isMissed ? (
                                                <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 13 }}>Missed</span>
                                            ) : (
                                                <span style={{ color: '#d97706', fontWeight: 700, fontSize: 13 }}>Pending Evaluation</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #d5f3d8' }}>
                                            <span style={mkBadge(r.status || 'completed')}>{r.status || 'Completed'}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Reusable Submission Review Modal */}
            <SubmissionReviewModal
                isOpen={viewingDetails}
                onClose={() => setViewingDetails(false)}
                data={selectedSubmission}
                apiBaseUrl={api.defaults.baseURL}
            />

            {detailLoading && (
                <div style={{ ...S.confirmOverlay, background: 'rgba(255,255,255,0.7)', zIndex: 2000 }}>
                    <div style={{ border: `4px solid #c8e6d8`, borderTopColor: '#27a065', borderRadius: '50%', width: 40, height: 40, animation: 'spin 0.8s linear infinite' }} />
                    <p style={{ marginTop: 16, fontWeight: 700, color: C.primary, fontFamily: font }}>Fetching submission details...</p>
                </div>
            )}
        </div>
    );
};

const badgeMap = {
    approved: { bg: 'rgba(39,160,101,0.10)', color: '#27a065' },
    LIVE: { bg: 'rgba(39,160,101,0.15)', color: '#1e8c58' },
    DRAFT: { bg: 'rgba(107,114,128,0.10)', color: '#6b7280' },
    LOCKED: { bg: 'rgba(90,180,212,0.12)', color: '#5ab4d4' },
    ENDED: { bg: 'rgba(107,114,128,0.10)', color: '#6b7280' },
    MISSED: { bg: 'rgba(239,68,68,0.10)', color: '#ef4444' },
};
const mkBadge = (status) => {
    const c = badgeMap[status] || badgeMap.DRAFT;
    return { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: c.bg, color: c.color, fontFamily: "'Outfit', sans-serif", textTransform: 'uppercase', letterSpacing: 0.5 };
};

export default StudentDashboard;
